import defaultAxios from 'axios';
import { isEmpty, join } from 'lodash';

import { axios, BASE_URL } from 'common/axios';

import { SystemUser } from 'common/types/system-users';

export default {
  initTagsData: (): Promise<any> => {
    const url = `${BASE_URL}/api/categories`;

    return fetch(url, { credentials: 'include' }).then((response) =>
      response.json(),
    );
  },
  uploadLdapCertificate: async (body: any): Promise<any> => {
    const url = '/api/applications/ldap/certificate/upload';
    const { data } = await axios.post(url, body);

    return data;
  },
  getAdvancedSearchResults: async (
    offset: number = 0,
    size: number = 50,
    type: string,
    rules: any,
    cancel: any,
    sortInfo: any,
  ): Promise<any> => {
    if (cancel) {
      cancel.cancel();
    }
    cancel = defaultAxios.CancelToken.source();

    const { data } = await axios.post(
      '/api/search/advanced',
      {
        offset,
        size,
        type,
        rules,
        ...(sortInfo
          ? { sort: { field: sortInfo.sortBy, order: sortInfo.sortDirection } }
          : {}),
      },
      { cancelToken: cancel.token },
    );

    return data;
  },
  getFilesById: (id: string): Promise<any> => {
    const url = `${BASE_URL}/api/category/files?id=${id}`;

    return fetch(url, {
      credentials: 'include',
    }).then((response) => response.json());
  },
  searchAllByField: async (
    field: any,
    size: any,
    exclude_values: string = '',
  ): Promise<any> => {
    const url = '/api/byallfield/';
    const { data } = await axios.post(url, {
      field: field,
      size: size,
      exclude_values: exclude_values,
    });

    return data;
  },
  getSources: async (): Promise<any> => {
    const url = '/api/sources/';
    const { data } = await axios.get(url);

    return data;
  },
  deleteSource: async (sourceId: string): Promise<any> => {
    const url = `/api/sources?id=${sourceId}`;
    const { data } = await axios.delete(url);

    return data;
  },
  updateCategory: async (threshold: any, categoryId: string): Promise<any> => {
    const url = '/api/update-category';
    const { data } = await axios.post(url, {
      categoryId: categoryId,
      threshold: threshold,
    });

    return data;
  },
  getMapData: async (
    main: any,
    sub: any,
    exclude_values: string = '',
  ): Promise<any> => {
    const url = '/api/map/';
    const { data } = await axios.post(url, {
      main,
      sub,
      exclude_values: exclude_values,
    });

    return data;
  },
  exportToCSV: async (body: any): Promise<any> => {
    let url = '';

    url = '/api/export-csv/';
    const { data } = await axios.post(url, body, {
      responseType: 'arraybuffer',
    });

    return data;
  },
  downloadCSV: async (name: string): Promise<any> => {
    const url = `/api/files/download-csv?name=${name}`;
    const { data } = await axios.get(url);

    return data;
  },
  downloadFile: async (
    file_id: string,
    convert: any,
    previewPDFPagesLimit: any = null,
  ): Promise<any> => {
    const url = '/api/download-file/';

    try {
      const { data } = await axios.post(
        url,
        {
          file_id,
          convert,
          pages_limit: previewPDFPagesLimit,
        },
        {
          responseType: 'blob',

          timeout: 30000,
        },
      );

      return data;
    } catch (err) {
      if (err.response.status === 503) {
        throw new Error(
          `${err.response.status} resource currently unavailable`,
        );
      }
      throw new Error(`${err.response.status} not found`);
    }
  },
  previewFile: async (file_id: string): Promise<any> => {
    const url = `/api/preview/?file_id=${file_id}`;
    const { data } = await axios.get(url, {
      timeout: 30000,
    });

    return data;
  },
  removeFile: async (file: any): Promise<any> => {
    const url = `/api/search?id=${file._id}`;
    const { data } = await axios.delete(url);

    return data;
  },
  getMessageAttachments: async (messageId: string): Promise<any> => {
    const url = encodeURI(
      `/api/mail/get-attachments?messageId=${encodeURIComponent(messageId)}`,
    );
    const { data } = await axios.get(url);

    return data;
  },
  clusteringData: async (): Promise<any> => {
    const url = '/api/clusters/';
    const { data } = await axios.get(url);

    return data;
  },
  clusteringPathData: async (clusterId: string): Promise<any> => {
    const url = `/api/clusters/${clusterId}/paths/`;
    const { data } = await axios.get(url);

    return data;
  },
  clusteringUpdateSensitivity: async (
    clusterId: string,
    newSensitivityId: string,
  ) => {
    const url = `/api/clusters/${clusterId}/sensitivity/`;
    const { data } = await axios.put(url, {
      cluster_sensitivity: newSensitivityId,
    });

    return data;
  },
  getSensitivities: async (): Promise<any> => {
    const url = '/api/sensitivities/';
    const { data } = await axios.get(url);

    return data;
  },
  getUsers: async (): Promise<any> => {
    const url = '/api/users/';
    const { data } = await axios.get(url);

    return data;
  },
  addNewUser: async (user: any): Promise<any> => {
    const url = '/api/users/';
    const { data } = await axios.post(url, {
      ...user,
      groups: user.roles,
    });

    return data;
  },
  deleteUser: async (user: any): Promise<any> => {
    const url = `/api/users?id=${user.id}`;
    const { data } = await axios.delete(url);

    return data;
  },
  resetUserPassword: async (user: SystemUser): Promise<any> => {
    const url = `/api/users/reset-password?id=${user.id}`;
    const { data } = await axios.put(url);

    return data;
  },
  setUserPassword: async (token: string, password: string): Promise<any> => {
    const url = `/api/anonymous/set-password?token=${token}`;
    const { data } = await axios.put(url, { password });

    return data;
  },
  updateUser: async (user: any): Promise<any> => {
    const url = '/api/users/';
    const { data } = await axios.put(url, {
      ...user,
      groups: user.roles,
    });

    return data;
  },
  quarantineFiles: async (
    rules: any,
    fileIdsList: any,
    deviceType: any,
  ): Promise<any> => {
    const url = '/api/quarantine/';
    const { data } = await axios.post(url, {
      rules,
      list_ids: fileIdsList,
      device_type: deviceType,
    });

    return data;
  },
  deleteFiles: async (
    rules: any,
    fileIdsList: any,
    deviceType: any,
  ): Promise<any> => {
    const url = '/api/delete/';
    const { data } = await axios.post(url, {
      rules,
      list_ids: fileIdsList,
      device_type: deviceType,
    });

    return data;
  },
  setFilesLabel: async (
    rules: any,
    fileIdsList: any,
    deviceType: any,
    categoryId: string,
    isSetLabel: any,
    is_remove: any,
  ): Promise<any> => {
    const url = '/api/application/files/label/';
    const { data } = await axios.post(url, {
      rules,
      list_ids: fileIdsList,
      device_type: deviceType,
      category_id: categoryId,
      set_label: isSetLabel,
      is_remove: is_remove,
    });

    return data;
  },
  revokePermissions: async (
    rules: any,
    fileIdsList: any,
    revokeRules: any,
  ): Promise<any> => {
    const url = '/api/revoke-permissions/';
    const { data } = await axios.post(url, {
      rules,
      file_ids: fileIdsList,
      revoke_rules: revokeRules,
    });

    return data;
  },
  getFeatures: async (): Promise<any> => {
    try {
      const url = '/api/system-features/';
      const { data } = await axios.get(url, {});

      return data;
    } catch (exception) {
      console.error(
        `Failed to load features - ${
          exception?.message || exception
        }. using default settings as fallback.`,
      );

      return { demo: false };
    }
  },
  getSettings: async ({
    token,
    tenantName,
  }: {
    token: string;
    tenantName: string;
  }): Promise<any> => {
    const queryParams = [];

    if (token) {
      queryParams.push(`token=${token}`);
    }

    if (tenantName) {
      queryParams.push(`tenant_name=${tenantName}`);
    }

    const queryParamsString = isEmpty(queryParams)
      ? ''
      : `?${join(queryParams, '&')}`;

    const url = `/api/system-settings${queryParamsString}`;

    const { data } = await axios.get(url);

    return data;
  },
};
